import { ActionIcon, Box, Group, Paper, Stack, Text } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useId } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { currencySymbol } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { NumberInputWithLabel } from 'src/design-system/NumberInputWithLabel';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { GroupMember } from '../../grouping';
import { MemberPicker } from '../MemberPicker';

export type AmountField = {
  key: string;
  label: string;
  /**
   * The key of another amount on the same card this one cannot come to more than — income after
   * tax cannot exceed income before tax. The two are filled in either order, so the pair only
   * counts as wrong once both are recorded.
   */
  mustNotExceed?: string;
};

export type Allocation = {
  clientId: string;
  itemId?: string;
  amounts: Record<string, number | ''>;
};

export type AllocationConfig = {
  members: GroupMember[];
  allocations: Allocation[];
  amountFields: AmountField[];
  /** Appended to each amount label, e.g. "(monthly)". */
  amountQualifier?: string;
  emptyLabel: string;
  /** Fixes who the item applies to, leaving only each member's own figures editable. */
  membersLocked?: boolean;
  onAdd: (clientId: string) => void;
  onRemove: (clientId: string) => void;
  onAmountChange: (clientId: string, key: string, value: number | '') => void;
};

/** "Who does this apply to?" — one card per member, each carrying that member's own figures. */
export function MemberAllocations({
  members,
  allocations,
  amountFields,
  amountQualifier,
  emptyLabel,
  membersLocked = false,
  onAdd,
  onRemove,
  onAmountChange,
}: AllocationConfig) {
  const currency = useCurrency();
  // Each label is tied to its own field by id, the association a `label` prop would have made.
  const idPrefix = useId();
  const fieldId = (clientId: string, key: string) => `${idPrefix}-${clientId}-${key}`;
  const memberByClientId = new Map(members.map(member => [member.clientId, member]));
  const selectedClientIds = allocations.map(allocation => allocation.clientId);

  return (
    <Stack gap={16} align="flex-start" style={{ alignSelf: 'stretch' }}>
      <Group justify="space-between" align="center" w="100%" wrap="nowrap">
        <Text
          c="gray.10"
          fw={FONT_WEIGHTS.semibold}
          style={{ fontSize: 16, lineHeight: '24px', whiteSpace: 'nowrap' }}
        >
          Who does this apply to?
        </Text>
        {!membersLocked && (
          <MemberPicker members={members} selectedClientIds={selectedClientIds} onSelect={onAdd} />
        )}
      </Group>

      {allocations.length === 0 ? (
        <Text c="gray.7" fw={FONT_WEIGHTS.medium} style={{ fontSize: 14, lineHeight: '20px' }}>
          {emptyLabel}
        </Text>
      ) : (
        <Stack gap="sm">
          {allocations.map(allocation => {
            const member = memberByClientId.get(allocation.clientId);
            const name = member
              ? formatName(member.firstName, member.lastName, NameFormat.NATURAL)
              : 'Unknown member';

            return (
              <Paper
                key={allocation.clientId}
                withBorder
                radius="md"
                p={12}
                bd="1px solid var(--mantine-color-gray-1)"
                data-testid={`allocation-${allocation.clientId}`}
              >
                <Group justify="space-between" mb={8}>
                  <Text size="sm" fw={FONT_WEIGHTS.medium} c="black">
                    {name}
                  </Text>
                  {!membersLocked && (
                    <ActionIcon
                      variant="subtle"
                      color="gray.8"
                      onClick={() => onRemove(allocation.clientId)}
                      aria-label={`Remove ${member?.firstName ?? 'member'}`}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Group>
                {/* A grid of two rows rather than a row of labelled fields: every label shares
                    the first row and every input the second, so the inputs start level however
                    many lines a label takes. Laid side by side instead, a label wrapping in a
                    narrow modal — "Amount before tax (monthly)" — pushed its own input down
                    past the one beside it, and a field carrying an error would do the same from
                    below. The labels are rendered here rather than by the field so that each
                    row is the grid's to place. */}
                <Box
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${amountFields.length}, minmax(0, 1fr))`,
                    columnGap: 'var(--mantine-spacing-md)',
                  }}
                >
                  {amountFields.map(field => (
                    <Text
                      key={`${field.key}-label`}
                      component="label"
                      htmlFor={fieldId(allocation.clientId, field.key)}
                      size="xs"
                      fw={FONT_WEIGHTS.semibold}
                      c="gray.6"
                      mb={6}
                      style={{ alignSelf: 'end' }}
                    >
                      {amountQualifier ? `${field.label} (${amountQualifier})` : field.label}
                    </Text>
                  ))}
                  {amountFields.map(field => (
                    <NumberInputWithLabel
                      key={field.key}
                      id={fieldId(allocation.clientId, field.key)}
                      leftSection={currencySymbol(currency)}
                      value={allocation.amounts[field.key] ?? ''}
                      onChange={value =>
                        onAmountChange(allocation.clientId, field.key, toAmount(value))
                      }
                      error={capError(allocation, field, amountFields)}
                      min={0}
                      decimalScale={2}
                      fixedDecimalScale
                      placeholder="0.00"
                      hideControls
                    />
                  ))}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Why a figure cannot stand beside the one it is capped by, for the field to say under itself.
 * Blank while either is missing: a part-filled pair is unfinished rather than contradictory.
 */
function capError(
  allocation: Allocation,
  field: AmountField,
  fields: AmountField[],
): string | undefined {
  if (!field.mustNotExceed) return undefined;
  const value = allocation.amounts[field.key];
  const cap = allocation.amounts[field.mustNotExceed];
  if (typeof value !== 'number' || typeof cap !== 'number' || value <= cap) return undefined;
  const capLabel = fields.find(candidate => candidate.key === field.mustNotExceed)?.label;
  return capLabel ? `Cannot be more than ${capLabel.toLowerCase()}` : 'Amount is too high';
}

function toAmount(value: string | number): number | '' {
  if (value === '' || value === null) return '';
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
}

// An amount left empty is not recorded yet, which is allowed: it saves as missing, not as zero
function isValidAmount(value: number | '' | undefined): boolean {
  if (value === '' || value === undefined) return true;
  return Number.isFinite(value) && value >= 0;
}

/** Whether every member's figures can be saved as they stand: present, positive, and consistent. */
export function allocationsComplete(allocations: Allocation[], fields: AmountField[]): boolean {
  return (
    allocations.length > 0 &&
    allocations.every(allocation =>
      fields.every(
        field =>
          isValidAmount(allocation.amounts[field.key]) &&
          capError(allocation, field, fields) === undefined,
      ),
    )
  );
}

/** Null when no member has the amount recorded, so the preview can say so rather than £0. */
export function sumAmounts(allocations: Allocation[], key: string): number | null {
  const recorded = allocations
    .map(allocation => allocation.amounts[key])
    .filter((value): value is number => typeof value === 'number');
  return recorded.length === 0 ? null : recorded.reduce((total, value) => total + value, 0);
}
